/**
 * AgentRail — the agent panel pinned to the right of every platform page.
 *
 * Collapsed it is a single tab showing who is on duty for this page; open it is
 * a narrow thread. Nothing here owns navigation state: when an agent offers a
 * destination the user clicks it, so the page never moves under them without a
 * deliberate act.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useQuery } from '@tanstack/react-query';
import { ArrowUp, ChevronDown, Loader2, PanelRightClose, RotateCcw, Sparkles, X } from 'lucide-react';
import { cn } from '@/components/ui/utils';
import { coworkApi } from '@/cowork/omniad/api/coworkApi';
import { avatarSrc } from '@/cowork/omniad/avatarSrc';
import type { Agent } from '@/cowork/omniad/types/cowork';
import { useAgentRail } from './AgentRailContext';
import { startersForPage } from './pageAgent';
import { CampaignWorkCard } from './CampaignWorkCard';
import { DraftReviewCard } from './DraftReviewCard';

export const AGENT_RAIL_WIDTH_CLASS = 'lg:mr-[380px]';

function Avatar({ agent, size = 28 }: { agent?: Agent; size?: number }) {
  return (
    <div
      className="flex shrink-0 items-center justify-center rounded-full text-sm"
      style={{
        width: size,
        height: size,
        backgroundColor: agent?.accentColor ? `${agent.accentColor}1a` : '#be43ff1a',
      }}
      aria-hidden
    >
      {agent?.avatarUrl ? (
        <img src={avatarSrc(agent)} alt="" className="h-full w-full rounded-full object-cover" />
      ) : (
        <span style={{ fontSize: size * 0.5 }}>{agent?.avatarEmoji ?? '✨'}</span>
      )}
    </div>
  );
}

/**
 * Agent replies are markdown — they arrive full of bold, lists and tables.
 * Rendered as plain text they showed literal `**` and `| --- |` rows, which is
 * what most of a campaign status answer looks like.
 *
 * Styling is tuned for a 380px column: tight leading, small type, and tables
 * that scroll inside their own box so the panel itself never scrolls sideways.
 */
function AgentMarkdown({ text }: { text: string }) {
  return (
    <div className="text-sm leading-relaxed text-[#344054] [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => <p className="my-2">{children}</p>,
          strong: ({ children }) => <strong className="font-semibold text-[#101828]">{children}</strong>,
          ul: ({ children }) => <ul className="my-2 list-disc space-y-1 pl-4">{children}</ul>,
          ol: ({ children }) => <ol className="my-2 list-decimal space-y-1 pl-4">{children}</ol>,
          li: ({ children }) => <li className="pl-0.5">{children}</li>,
          h1: ({ children }) => <h3 className="mb-1 mt-3 text-[13px] font-semibold text-[#101828]">{children}</h3>,
          h2: ({ children }) => <h3 className="mb-1 mt-3 text-[13px] font-semibold text-[#101828]">{children}</h3>,
          h3: ({ children }) => <h3 className="mb-1 mt-3 text-[13px] font-semibold text-[#101828]">{children}</h3>,
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noreferrer" className="text-[#be43ff] underline underline-offset-2">
              {children}
            </a>
          ),
          code: ({ children }) => (
            <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-[11px] text-[#344054]">{children}</code>
          ),
          pre: ({ children }) => (
            <pre className="my-2 overflow-x-auto rounded-lg bg-gray-50 p-2 text-[11px]">{children}</pre>
          ),
          hr: () => <hr className="my-3 border-gray-100" />,
          blockquote: ({ children }) => (
            <blockquote className="my-2 border-l-2 border-gray-200 pl-2.5 text-[#667085]">{children}</blockquote>
          ),
          table: ({ children }) => (
            <div className="my-2 overflow-x-auto rounded-lg border border-gray-100">
              <table className="w-full border-collapse text-[11px]">{children}</table>
            </div>
          ),
          thead: ({ children }) => <thead className="bg-gray-50">{children}</thead>,
          th: ({ children }) => (
            <th className="border-b border-gray-100 px-2 py-1.5 text-left font-semibold text-[#475467]">{children}</th>
          ),
          td: ({ children }) => (
            <td className="border-b border-gray-50 px-2 py-1.5 align-top text-[#344054]">{children}</td>
          ),
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}

export function AgentRail() {
  const {
    available, open, setOpen, agentId, pinnedAgentId, pinAgent,
    messages, streaming, ask, clearThread, campaignId, page,
  } = useAgentRail();
  const [value, setValue] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: agents = [] } = useQuery({
    queryKey: ['cowork', 'agents'],
    queryFn: () => coworkApi.agents.list(),
    enabled: available,
    staleTime: 5 * 60_000,
  });

  const agent = agents.find((a) => a.id === agentId);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  if (!available) return null;

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    const text = value.trim();
    if (!text || streaming) return;
    setValue('');
    ask(text);
  };

  const go = (path: string) =>
    window.dispatchEvent(new CustomEvent('agent-rail-navigate', { detail: path }));

  /* ── Collapsed: a tab showing who is on duty here ── */
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed right-0 top-1/2 z-40 hidden -translate-y-1/2 items-center gap-2 rounded-l-2xl border border-r-0 border-gray-200 bg-white py-3 pl-3 pr-2.5 shadow-lg transition-all hover:pr-4 lg:flex"
        title={agent ? `Ask ${agent.name}` : 'Ask your agents'}
      >
        <Avatar agent={agent} />
        <span className="text-xs font-medium text-gray-700">{agent?.name ?? 'Agents'}</span>
      </button>
    );
  }

  /* ── Open ── */
  return (
    <aside className="fixed bottom-0 right-0 top-14 z-40 hidden w-[380px] flex-col border-l border-gray-200 bg-white shadow-2xl min-[1440px]:shadow-none lg:flex">
      {/* Header — who is answering, and why */}
      <header className="relative flex items-center gap-2.5 border-b border-gray-100 px-4 py-3">
        <Avatar agent={agent} size={32} />
        <button
          type="button"
          onClick={() => setPickerOpen((v) => !v)}
          className="flex min-w-0 flex-1 items-center gap-1 text-left"
        >
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold text-[#101828]">
              {agent?.name ?? 'Your agents'}
            </span>
            <span className="block truncate text-[11px] text-[#667085]">
              {pinnedAgentId ? 'Pinned' : agent?.title ?? 'On this page'}
            </span>
          </span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden />
        </button>

        {messages.length > 0 && (
          <button
            type="button"
            onClick={clearThread}
            title="Start over"
            className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600"
          >
            <RotateCcw className="h-4 w-4" aria-hidden />
          </button>
        )}
        <button
          type="button"
          onClick={() => setOpen(false)}
          title="Hide panel"
          className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600"
        >
          <PanelRightClose className="h-4 w-4" aria-hidden />
        </button>

        {pickerOpen && (
          <div className="absolute left-3 right-3 top-full z-50 mt-1 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-xl">
            {pinnedAgentId && (
              <button
                type="button"
                onClick={() => { pinAgent(null); setPickerOpen(false); }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-[#be43ff] hover:bg-gray-50"
              >
                <X className="h-3.5 w-3.5" aria-hidden />
                Follow the page again
              </button>
            )}
            {agents.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => { pinAgent(a.id); setPickerOpen(false); }}
                className={cn(
                  'flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-gray-50',
                  a.id === agentId && 'bg-[#be43ff]/5',
                )}
              >
                <Avatar agent={a} size={24} />
                <span className="min-w-0">
                  <span className="block truncate text-xs font-medium text-[#101828]">{a.name}</span>
                  <span className="block truncate text-[11px] text-[#667085]">{a.title}</span>
                </span>
              </button>
            ))}
          </div>
        )}
      </header>

      {campaignId && <CampaignWorkCard campaignId={campaignId} />}
      {campaignId && <DraftReviewCard campaignId={campaignId} />}

      {/* Thread */}
      <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
        {messages.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center">
            <Sparkles className="h-6 w-6 text-[#be43ff]/40" aria-hidden />
            <p className="text-sm text-[#344054]">
              {agent ? `${agent.name} is on this page with you.` : 'Your agents are here.'}
            </p>
            <p className="text-xs text-[#98a2b3]">
              Ask about what you&apos;re looking at, or hand over the work entirely.
            </p>
            <div className="mt-2 flex w-full flex-col gap-1.5">
              {startersForPage(page).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => ask(s)}
                  className="rounded-xl border border-gray-100 px-3 py-2 text-left text-[12px] text-[#475467] transition-colors hover:border-[#be43ff]/30 hover:bg-[#be43ff]/[0.03] hover:text-[#101828]"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={cn('flex', m.role === 'user' ? 'justify-end' : 'justify-start')}>
            <div className={cn('min-w-0 max-w-[92%]', m.role === 'user' && 'max-w-[85%]')}>
              {m.role === 'user' ? (
                <p className="rounded-2xl rounded-br-sm bg-[#be43ff] px-3.5 py-2 text-sm text-white">
                  {m.content}
                </p>
              ) : (
                <>
                  {m.progress && m.progress.length > 0 && (
                    <div className="mb-1.5 flex flex-wrap gap-1">
                      {m.progress.map((p) => (
                        <span
                          key={p}
                          className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-gray-500"
                        >
                          {p}
                        </span>
                      ))}
                    </div>
                  )}
                  {m.error ? (
                    <p className="whitespace-pre-wrap text-sm leading-relaxed text-red-600">{m.content}</p>
                  ) : m.content ? (
                    <AgentMarkdown text={m.content} />
                  ) : m.streaming ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-300" aria-hidden />
                  ) : null}

                  {m.tasks?.map((task) => (
                    <button
                      key={task.taskId}
                      type="button"
                      onClick={() => go('/agents/tasks')}
                      className="mt-2 flex w-full items-center gap-2 rounded-xl border border-gray-200 px-3 py-2 text-left transition-colors hover:border-[#be43ff]/40 hover:bg-[#be43ff]/5"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-xs font-medium text-[#101828]">
                          {task.title}
                        </span>
                        <span className="block text-[11px] text-[#667085]">
                          {task.agentName ? `Handed to ${task.agentName}` : 'In your task board'}
                        </span>
                      </span>
                    </button>
                  ))}

                  {m.actions?.map((action) => (
                    <button
                      key={action.path}
                      type="button"
                      onClick={() => go(action.path)}
                      className="mt-2 w-full rounded-xl bg-[#be43ff] px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-[#a935e6]"
                    >
                      {action.label}
                    </button>
                  ))}
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Composer */}
      <form onSubmit={submit} className="border-t border-gray-100 p-3">
        <div className="flex items-end gap-2 rounded-2xl bg-[#f8f9fa] p-1.5 pl-3.5 ring-1 ring-transparent focus-within:ring-[#be43ff]/40">
          <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            placeholder={agent ? `Ask ${agent.name}…` : 'Ask your agents…'}
            aria-label="Message your agent"
            disabled={streaming}
            className="min-w-0 flex-1 bg-transparent py-2 text-sm text-[#101828] outline-none placeholder:text-[#98a2b3] disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={!value.trim() || streaming}
            aria-label="Send"
            className={cn(
              'flex h-8 w-8 shrink-0 items-center justify-center rounded-xl transition-colors',
              value.trim() && !streaming
                ? 'bg-[#be43ff] text-white hover:bg-[#a935e6]'
                : 'bg-[#eaecf0] text-[#d0d5dd]',
            )}
          >
            {streaming ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <ArrowUp className="h-4 w-4" aria-hidden />
            )}
          </button>
        </div>
      </form>
    </aside>
  );
}
